import { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminChangeCredentials() {
  const { user, refresh } = useAuth();
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password updated.");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await api.post("/auth/change-email", emailForm);
      toast.success("Email updated. Please sign in again with the new email.");
      setEmailForm({ current_password: "", new_email: "" });
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <p className="section-label mb-1">Account Security</p>
        <h1 className="serif-display text-3xl md:text-4xl text-foreground">My Account</h1>
        <p className="mt-2 text-sm text-foreground/70">Update your login credentials. Signed in as <span className="text-gold">{user?.email}</span>.</p>
      </div>

      <form onSubmit={changePw} className="p-6 rounded-md border border-border/60 bg-card space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-gold/10 text-gold flex items-center justify-center"><KeyRound size={16} /></div>
          <p className="font-serif text-xl">Change Password</p>
        </div>
        <div>
          <Label>Current Password</Label>
          <Input type="password" maxLength={25} className="mt-2 rounded-sm" value={pwForm.current_password} onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))} required />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>New Password (max 25 chars)</Label>
            <Input type="password" maxLength={25} className="mt-2 rounded-sm" value={pwForm.new_password} onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))} required />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input type="password" maxLength={25} className="mt-2 rounded-sm" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} required />
          </div>
        </div>
        <Button type="submit" disabled={savingPw} className="rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold h-11 px-6 tracking-widest uppercase text-xs">
          {savingPw ? <><Loader2 size={13} className="animate-spin mr-2" /> Saving…</> : <><Save size={13} className="mr-2" /> Update Password</>}
        </Button>
      </form>

      <form onSubmit={changeEmail} className="p-6 rounded-md border border-border/60 bg-card space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-gold/10 text-gold flex items-center justify-center"><Mail size={16} /></div>
          <p className="font-serif text-xl">Change Email</p>
        </div>
        <div>
          <Label>Current Password (to confirm)</Label>
          <Input type="password" maxLength={25} className="mt-2 rounded-sm" value={emailForm.current_password} onChange={(e) => setEmailForm((f) => ({ ...f, current_password: e.target.value }))} required />
        </div>
        <div>
          <Label>New Email (max 40 chars)</Label>
          <Input type="email" maxLength={40} className="mt-2 rounded-sm" value={emailForm.new_email} onChange={(e) => setEmailForm((f) => ({ ...f, new_email: e.target.value }))} required />
        </div>
        <Button type="submit" disabled={savingEmail} className="rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold h-11 px-6 tracking-widest uppercase text-xs">
          {savingEmail ? <><Loader2 size={13} className="animate-spin mr-2" /> Saving…</> : <><Save size={13} className="mr-2" /> Update Email</>}
        </Button>
      </form>
    </div>
  );
}
